import { Button } from '@/components/ui/button'
import { useKnowledgeStore } from '@/stores/knowledgeStore'
import { ChevronLeft } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useState, useCallback, useMemo } from 'react'
import UpdateDatasetDialog from '@/components/Knowledge/UpdateDatasetDialog'
import { DatasetForm } from '@/components/Knowledge/UpdateDatasetDialog'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { useUpdateDataset } from '@/api/hooks/useDataset'
import { toast } from '@you/ui'
import { Dataset } from '@/api/types/dataset.type'
import { Icon } from '@/components/Icon'
import { KnowledgeDocType } from '@/api/types/knowledge.types'
import DatasetDetailText from '@/components/Knowledge/DatasetDetailText'
import DatasetDetailTable from '@/components/Knowledge/DatasetDetailTable'
import { BaseFile } from '@/components/common/FileCard'
import { useDatasetFiles } from '@/api/hooks/useDatasetFiles'
import { DatasetFile } from '@/api/types/datasetFiles.type'

const DatasetDetail = () => {
  const navigate = useNavigate()
  const { currentDataset, currentKnowledge, setCurrentDataset } =
    useKnowledgeStore()
  const { currentWorkspace } = useWorkspaceStore()
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [selectedFileUrl, setSelectedFileUrl] = useState('')

  // 使用数据集 hooks
  const { trigger: saveOrUpdateDataset } = useUpdateDataset()

  // 使用 useMemo 优化参数对象，避免每次渲染都创建新对象
  const datasetFilesParams = useMemo(
    () => ({
      datasetId: currentDataset?.id || '',
    }),
    [currentDataset?.id]
  )

  // 使用数据集文件 hooks
  const {
    fileList: apiFileList,
    // refresh: refreshFileList,s
    removeFile: deleteDatasetFile,
  } = useDatasetFiles(datasetFilesParams)

  // 转换文件列表格式
  const fileList: BaseFile[] = apiFileList.map((file: DatasetFile) => ({
    id: file.id,
    name: file.fileName,
    size: parseInt(file.fileSize),
  }))

  const handleBack = () => {
    navigate('/knowledge/dataset')
  }

  /**
   * 处理编辑数据集
   */
  const handleUpdateDataset = async (data: DatasetForm) => {
    if (!currentDataset) return

    setIsUpdating(true)
    try {
      const datasetData = {
        ...data,
        id: currentDataset.id,
        knowledgeBaseId: currentKnowledge?.id || '',
        workspaceId: currentWorkspace?.id || '',
      }

      await saveOrUpdateDataset(datasetData)
      setCurrentDataset({
        ...currentDataset,
        ...data,
      } as Dataset)
      toast.success('数据集更新成功')

      // 更新成功后关闭对话框
      setIsEditDialogOpen(false)
    } catch (error) {
      console.error('更新数据集失败:', error)
      toast.error('数据集更新失败')
    } finally {
      setIsUpdating(false)
    }
  }

  /**
   * 处理添加文件
   */
  const handleAddFile = () => {
    navigate('/knowledge/dataset-import')
  }

  /**
   * 处理删除文件
   */
  const handleDeleteFile = useCallback(
    async (fileId: string) => {
      try {
        await deleteDatasetFile(fileId)
        // 不需要手动调用 refreshFileList，因为 deleteDatasetFile 内部已经处理了数据更新
        toast.success('文件删除成功')
      } catch (error) {
        console.error('删除文件失败:', error)
        toast.error('文件删除失败')
      }
    },
    [deleteDatasetFile]
  )

  /**
   * 处理文件选择
   */
  const handleFileSelect = useCallback(
    (fileId: string) => {
      const selectedFile = apiFileList.find((file) => file.id === fileId)
      if (selectedFile) {
        setSelectedFileUrl(selectedFile.url)
      }
    },
    [apiFileList]
  )

  // 判断是否为文本类型
  const isTextType = currentKnowledge?.docType === KnowledgeDocType.TEXT

  // 移除不必要的 useEffect，因为 SWR 会自动处理数据获取
  // 当 currentDataset?.id 变化时，useDatasetFiles 会自动重新获取数据

  return (
    <div className="h-full flex flex-col">
      {/* 头部导航区域 */}
      <div className="flex items-center justify-between mb-2 flex-shrink-0">
        {/* 左侧：返回按钮和知识库信息 */}
        <div className="flex items-center space-x-4">
          {/* 返回按钮 */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBack}
            className="flex items-center space-x-2 p-0 h-auto"
          >
            <ChevronLeft className="h-6 w-6 text-black" />
          </Button>

          {/* 知识库名称和类型标签 */}
          <div className="flex items-center space-x-1">
            <h1 className="text-2xl font-semibold text-black">
              {currentDataset?.name || '数据集详情'}
            </h1>
            {currentDataset && (
              <Button
                variant="ghost"
                size="icon"
                className="text-[#8752FF] p-1"
                onClick={() => setIsEditDialogOpen(true)}
              >
                <Icon name="edit" size={16} />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* 内容区域 */}
      <div className="flex-1 overflow-x-auto">
        {/* 数据集详情组件 */}
        {isTextType ? (
          <DatasetDetailText
            fileList={fileList}
            onAddFile={handleAddFile}
            onDeleteFile={handleDeleteFile}
            onFileSelect={handleFileSelect}
            selectedFileUrl={selectedFileUrl}
          />
        ) : (
          <DatasetDetailTable
            fileList={fileList}
            onAddFile={handleAddFile}
            onDeleteFile={handleDeleteFile}
            onFileSelect={handleFileSelect}
          />
        )}
      </div>

      {/* 编辑数据集对话框 */}
      {currentDataset && (
        <UpdateDatasetDialog
          open={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
          mode="edit"
          initialData={{
            name: currentDataset.name,
            desc: currentDataset.desc,
            icon: currentDataset.icon,
          }}
          onSubmit={handleUpdateDataset}
          isSubmitting={isUpdating}
        />
      )}
    </div>
  )
}

export default DatasetDetail
